import React, { useEffect } from "react";
import * as Icons from "@wix/wix-ui-icons-common";
import {
  Box,
  Button,
  Card,
  WixDesignSystemProvider,
  MarketingPageLayout,
  MarketingPageLayoutContent,
  Page,
  Loader,
  SkeletonGroup,
  SkeletonRectangle,
  ToggleSwitch,
  FormField,
  SectionHelper,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import { dashboard } from "@wix/dashboard";
import { createClient } from "@wix/sdk";
import { ReactTagManager } from "react-gtm-ts";
import { appInstances, embeddedScripts } from "@wix/app-management";
import { products } from "@wix/stores";

function inIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

function App() {
  ReactTagManager.init(tagManagerArgs);
  const appId = "847d4cc0-ddca-4bec-8ebf-e235a7413f08";
  const [client, setClient] = React.useState(null as any);
  const [message, setMessage] = React.useState({
    _id: "847d4cc0-ddca-4bec-8ebf-e235a7413f08",
    title: "Show price only when member is signed in",
    description:
      "Make your product price visible only when the member is signed in",
    // image: "https://www.wix.com/favicon.ico",
  } as any);
  const [showFade, setShowFade] = React.useState(false);
  const [isEnabled, setIsEnabled] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  const token = new URLSearchParams(window.location.search).get("token");
  const instance = new URLSearchParams(window.location.search).get("instance");
  const [isUpgraded, setIsUpgraded] = React.useState(false);
  const [instanceData, setInstanceData] = React.useState({
    instance: {
      isFree: true,
      availablePlans: [],
      instanceId: "",
    },
  } as any);

  const URLS = {
    editorUrl: `https://www.wix.com/editor/${instanceData?.site?.siteId}`,
    upgradeUrl: `https://www.wix.com/apps/upgrade/${appId}?appInstanceId=${instanceData?.instanceId}`,
  };

  useEffect(() => {
    try {
      if (inIframe()) {
        var c = createClient({
          host: dashboard.host(),
          auth: dashboard.auth(),
          modules: {
            dashboard,
            appInstances,
            embeddedScripts,
            products
          },
        });
        setClient(c);
        c.appInstances.getAppInstance().then((instance) => {
          setInstanceData(instance);
        });

        // Check embedded script status
        c.embeddedScripts
          .getEmbeddedScript()
          .then((scriptProperties) => {
            setIsEnabled(!scriptProperties.disabled);
          })
          .catch((error) => {
            if (error.response?.status !== 404) {
              console.error("Failed to get script status:", error);
            }
            setIsEnabled(false);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    } catch (error) {
      throw error;
    }
  }, []);

  useEffect(() => {
    if (token && message?.redirectUrl) {
      window.location.href = `https://www.wix.com/installer/install?token=${token}&appId=${appId}&redirectUrl=${message.redirectUrl}`;
    }
  }, [token, message]);

  const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    setIsEnabled(value);
    if (client) {
      try {
        await client.embeddedScripts.embedScript(
          {
            disabled: !value,
            parameters: {
              instanceId: instanceData?.instance?.instanceId,
            },
          },
          { componentId: "f56a2b46-5f12-4081-8ca2-a53ea6c56925" }
        );
      } catch (error) {
        console.error("Failed to embed script:", error);
        setIsEnabled(!value);
      }
    }
  };

  if (token) {
    return (
      <WixDesignSystemProvider features={{ newColorsBranding: true }}>
        <Page height="100vh">
          <Page.Content>
            <Box height={"90vh"} direction="vertical" verticalAlign="middle">
              <Loader text="Loading" />
            </Box>
          </Page.Content>
        </Page>
      </WixDesignSystemProvider>
    );
  }

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      <Card>
        <MarketingPageLayout
          removeImageHorizontalPadding
          removeImageVerticalPadding
          content={
            <Box height="840px" verticalAlign="middle">
              {message._id ? (
                <MarketingPageLayoutContent
                  title={message.title}
                  content={message.description}
                  actions={
                    instanceData?.site?.url ? (
                      <Box gap="SP2" direction="vertical">
                        <FormField label="Enable Tool">
                          <ToggleSwitch
                            checked={isEnabled}
                            onChange={handleToggle}
                            disabled={isLoading}
                          />
                        </FormField>
                        <SectionHelper
                          appearance="preview"
                          actionText="Go to live site"
                          onAction={() => {
                            window.open(
                              instanceData?.site?.url + "?rc=FEEDBACK",
                              "_blank"
                            );
                          }}
                        >
                          The tool only works from live site.
                        </SectionHelper>
                      </Box>
                    ) : (
                      <SectionHelper
                        appearance="danger"
                        actionText="Publish site"
                        onAction={() => {
                          window.open(
                            "https://www.wix.com/editor/" +
                              instanceData?.site?.siteId,
                            "_blank"
                          );
                        }}
                      >
                        Publish your site, and come back to enable the tool.
                      </SectionHelper>
                    )
                  }
                />
              ) : (
                <MarketingPageLayoutContent
                  title={
                    <SkeletonGroup skin="light">
                      <Box gap="SP2" direction="vertical">
                        <SkeletonRectangle height={"72px"} width={"396px"} />
                      </Box>
                    </SkeletonGroup>
                  }
                  content={
                    <SkeletonGroup skin="light">
                      <Box gap="SP2" direction="vertical">
                        <SkeletonRectangle height={"48px"} width={"396px"} />
                      </Box>
                    </SkeletonGroup>
                  }
                  actions={
                    <Box gap="SP2">
                      <SkeletonGroup skin="light">
                        <SkeletonRectangle height={"36px"} width={"129px"} />
                      </SkeletonGroup>
                      <SkeletonGroup skin="light">
                        <SkeletonRectangle height={"36px"} width={"207px"} />
                      </SkeletonGroup>
                    </Box>
                  }
                />
              )}
            </Box>
          }
          image={
            message._id ? (
              message.image
            ) : (
              <SkeletonGroup skin="light">
                <SkeletonRectangle height={"936px"} width={"100%"} />
              </SkeletonGroup>
            )
          }
        />
      </Card>
    </WixDesignSystemProvider>
  );
}

export default App;
